import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Home, Menu, X, Zap, BarChart2, Target, Users, Utensils, Weight, User, LogOut, Dumbbell, Award, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import innateMark from '@/assets/innate-mark.png';

const AVATAR_EMOJIS = ['🌱', '🌿', '🪴', '🌸', '🌺', '🌻', '✨', '🌟'];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const navLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: <Home className="w-4 h-4" />, tid: 'nav-dashboard' },
    { name: 'Workouts', path: '/workouts', icon: <Dumbbell className="w-4 h-4" />, tid: 'nav-workouts' },
    { name: 'Meals', path: '/meals', icon: <Utensils className="w-4 h-4" />, tid: 'nav-meals' },
    { name: 'Goals', path: '/goals', icon: <Target className="w-4 h-4" />, tid: 'nav-goals' },
    { name: 'Groups', path: '/groups', icon: <Users className="w-4 h-4" />, tid: 'nav-groups' },
    { name: 'Weight', path: '/weight', icon: <Weight className="w-4 h-4" />, tid: 'nav-weight' },
    { name: 'Leaderboard', path: '/leaderboard', icon: <TrendingUp className="w-4 h-4" />, tid: 'nav-leaderboard' },
    { name: 'Shop', path: '/shop', icon: <Award className="w-4 h-4" />, tid: 'nav-shop' },
  ];

  return (
    <nav className="bg-white border-b border-[#D4C4B0] sticky top-0 z-50 innate-shadow" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* BRANDING SECTION */}
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <img src={innateMark} alt="Innate" className="h-10 group-hover:rotate-12 transition-transform" />
            <div className="hidden sm:block">
              <h1 className="text-xl font-black uppercase tracking-tighter text-[#5C4A42] group-hover:text-[#D4A574] transition-colors">
                INNATE
              </h1>
              <p className="text-[10px] text-[#8B7355] uppercase font-bold tracking-widest -mt-1">From within</p>
            </div>
          </Link>

          {/* DESKTOP NAVIGATION (Hidden on Mobile) */}
          <div className="hidden xl:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link key={link.name} to={link.path}>
                <Button variant="ghost" size="sm" className="text-[#8B7355] hover:text-[#D4A574] hover:bg-[#FAF9F7] uppercase font-bold text-xs" data-testid={link.tid}>
                  {link.icon}
                  <span className="ml-2">{link.name}</span>
                </Button>
              </Link>
            ))}
          </div>

          {/* USER STATS & ACTIONS (Desktop) */}
          <div className="hidden md:flex items-center gap-3">
            <div className="h-6 w-px bg-[#D4C4B0] mx-2"></div>

            {/* XP / Level Badge */}
            <Link to="/progress">
              <Button variant="ghost" size="sm" className="bg-[#FAF9F7] border border-[#D4C4B0] text-[#5C4A42] hover:border-[#D4A574]/50" data-testid="nav-progress">
                <span className="text-lg mr-2">{AVATAR_EMOJIS[(user.avatar_stage || 1) - 1]}</span>
                <span className="text-xs font-black text-[#D4A574]">LV {user.level || 1}</span>
              </Button>
            </Link>

            {/* Points Indicator (Earthy Gradient) */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#5C4A42] to-[#8B7355] text-white rounded-lg shadow-lg">
              <Zap className="w-3 h-3 fill-[#D4A574] text-[#D4A574]" />
              <span className="font-black text-sm">{user.points || 0}</span>
            </div>

            <Link to="/profile">
              <Button variant="ghost" size="icon" className="text-[#8B7355] hover:text-[#5C4A42]" data-testid="nav-profile">
                <User className="w-5 h-5" />
              </Button>
            </Link>

            <Button
              variant="ghost" size="icon"
              onClick={handleLogout}
              className="text-[#8B7355] hover:text-[#FF3300]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>

          {/* MOBILE HAMBURGER BUTTON */}
          <div className="xl:hidden flex items-center gap-4">
            {/* Mobile Points View */}
            <div className="flex items-center gap-1 text-[#D4A574] font-black text-sm md:hidden">
              <Zap className="w-4 h-4 fill-current" />
              {user.points || 0}
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-[#5C4A42] bg-[#FAF9F7] rounded-lg border border-[#D4C4B0]"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* RESPONSIVE MOBILE DRAWER */}
      {isOpen && (
        <div className="xl:hidden bg-white border-t border-[#D4C4B0] absolute w-full h-screen animate-in slide-in-from-right overflow-y-auto pb-20">
          <div className="p-4 space-y-2">
            {/* Mobile Progress Card */}
            <div className="bg-[#FAF9F7] p-6 rounded-2xl border border-[#D4C4B0] mb-6">
               <div className="flex items-center gap-4">
                  <div className="text-5xl bg-white p-3 rounded-full border border-[#D4A574]/30">
                    {AVATAR_EMOJIS[(user.avatar_stage || 1) - 1]}
                  </div>
                  <div>
                    <p className="text-[#8B7355] text-xs font-black uppercase tracking-widest">Current Rank</p>
                    <h3 className="text-[#5C4A42] text-2xl font-black italic">LEVEL {user.level || 1}</h3>
                  </div>
               </div>
            </div>

            {/* Mobile Link List */}
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-between p-4 rounded-xl bg-[#FAF9F7] border border-transparent hover:border-[#D4A574]/50 hover:bg-[#D4A574]/5 transition-all"
              >
                <div className="flex items-center gap-4">
                  <span className="text-[#D4A574]">{link.icon}</span>
                  <span className="text-[#5C4A42] font-black uppercase tracking-tight">{link.name}</span>
                </div>
                <Zap size={16} className="text-[#D4C4B0]" />
              </Link>
            ))}

            <div className="pt-6 grid grid-cols-2 gap-3">
              <Button
                onClick={() => { setIsOpen(false); navigate('/profile'); }}
                className="bg-[#D4C4B0] text-[#5C4A42] font-bold h-12 rounded-xl"
              >
                PROFILE
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-[#FF3300]/10 text-[#FF3300] border border-[#FF3300]/20 font-bold h-12 rounded-xl"
              >
                LOGOUT
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
